"use client";

import { useEffect, useState, useTransition } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";

const DEBOUNCE_MS = 150;

export function SearchBar({
  placeholder = "Search varieties (e.g. Freedom, hydrangea, red)",
}: {
  placeholder?: string;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const initialQ = searchParams.get("q") ?? "";
  const [value, setValue] = useState(initialQ);

  // Debounced router push - update the URL `q` param 150ms after the user
  // stops typing. Empty string removes the param.
  useEffect(() => {
    if (value === initialQ) return;
    const handle = setTimeout(() => {
      const params = new URLSearchParams(searchParams);
      if (value.trim().length === 0) {
        params.delete("q");
      } else {
        params.set("q", value);
      }
      const qs = params.toString();
      startTransition(() => {
        router.push(qs ? `${pathname}?${qs}` : pathname);
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  function clear() {
    setValue("");
    const params = new URLSearchParams(searchParams);
    params.delete("q");
    const qs = params.toString();
    router.push(qs ? `${pathname}?${qs}` : pathname);
  }

  return (
    <div data-testid="catalog-search" className="relative w-full">
      <input
        type="search"
        data-testid="catalog-search-input"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        aria-label="Search catalog"
        className="w-full rounded-full border border-brand-border bg-white px-5 py-2.5 pr-12 text-base text-brand-text placeholder:text-brand-text-muted focus:border-brand-teal focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
      />
      {value.length > 0 && (
        <button
          type="button"
          data-testid="catalog-search-clear"
          aria-label="Clear search"
          onClick={clear}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full px-2 py-1 text-xs font-semibold text-brand-text-muted hover:bg-brand-surface hover:text-brand-text"
        >
          Clear
        </button>
      )}
      {isPending && (
        <span
          data-testid="catalog-search-pending"
          className="absolute -bottom-5 left-2 text-[11px] text-brand-text-muted"
        >
          Searching...
        </span>
      )}
    </div>
  );
}
